import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
  type Entity,
} from '@backstage/catalog-model';
import { compatWrapper } from '@backstage/core-compat-api';
import {
  EntityContentBlueprint,
  EntityContextMenuItemBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  EntityDependencyOfComponentsCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
} from '@backstage/plugin-catalog';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  Router as KubernetesRouter,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import { Router as DatadogRouter } from '@roadiehq/backstage-plugin-datadog';
import { EntityImplementsFeaturesCard } from '@schibsted/backstage-plugin-feature-react';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@backstage/ui';
import { RiCoupon3Line, RiEyeLine } from '@remixicon/react';
import { useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import EntityDatadogReferrerWrapper from '../../components/catalog/EntityDatadogReferrerWrapper';
import { EntityBadgesDialog } from '@backstage-community/plugin-badges';

let badgesDialogOpen = false;
const badgesListeners = new Set<() => void>();
function notifyBadgesListeners() {
  badgesListeners.forEach(fn => {
    fn();
  });
}
export function openBadgesDialog() {
  badgesDialogOpen = true;
  notifyBadgesListeners();
}
function closeBadgesDialog() {
  badgesDialogOpen = false;
  notifyBadgesListeners();
}
function useBadgesDialogOpen() {
  return useSyncExternalStore(
    onStoreChange => {
      badgesListeners.add(onStoreChange);
      return () => badgesListeners.delete(onStoreChange);
    },
    () => badgesDialogOpen,
  );
}

const BadgesDialog = () => {
  const open = useBadgesDialogOpen();
  return <EntityBadgesDialog open={open} onClose={closeBadgesDialog} />;
};

function isFeatureFlagActive(name: string): boolean {
  try {
    const flags: Record<string, number> = JSON.parse(
      localStorage.getItem('featureFlags') ?? '{}',
    );
    return flags[name] === 1;
  } catch {
    return false;
  }
}

const isStandardComponent = (entity: Entity) =>
  entity.kind.toLowerCase() === 'component' &&
  ['service', 'library', 'website'].includes(
    (entity.spec?.type as string)?.toLowerCase() ?? '',
  );

const isServiceComponent = (entity: Entity) =>
  entity.kind.toLowerCase() === 'component' &&
  (entity.spec?.type as string)?.toLowerCase() === 'service';

const isOpenApiEntity = (entity: Entity) =>
  entity.kind.toLowerCase() === 'api' &&
  (entity.spec?.type as string)?.toLowerCase() === 'openapi';

const TwoColumnGrid = ({ children }: { children: ReactNode }) => (
  <Grid.Root columns={{ initial: '1', md: '2' }} gap="6">
    {children}
  </Grid.Root>
);

export const overviewContent = EntityContentBlueprint.make({
  name: 'overview',
  params: {
    path: '/',
    title: 'Overview',
    group: 'overview',
    loader: async () => {
      const { OverviewContent } = await import('./OverviewContent');
      return compatWrapper(
        <>
          <BadgesDialog />
          <OverviewContent />
        </>,
      );
    },
  },
});

export const kubernetesContent = EntityContentBlueprint.make({
  name: 'kubernetes',
  params: {
    path: '/kubernetes',
    title: 'Kubernetes',
    filter: (entity: Entity) =>
      isServiceComponent(entity) && isKubernetesAvailable(entity),
    loader: async () => compatWrapper(<KubernetesRouter />),
  },
});

export const dependenciesContent = EntityContentBlueprint.make({
  name: 'dependencies',
  params: {
    path: '/dependencies',
    title: 'Dependencies',
    filter: isStandardComponent,
    loader: async () =>
      compatWrapper(
        <TwoColumnGrid>
          <Grid.Item>
            <EntityDependsOnComponentsCard />
          </Grid.Item>
          <Grid.Item>
            <EntityDependencyOfComponentsCard />
          </Grid.Item>
          <Grid.Item>
            <EntityDependsOnResourcesCard />
          </Grid.Item>
        </TwoColumnGrid>,
      ),
  },
});

export const featuresContent = EntityContentBlueprint.make({
  name: 'features',
  params: {
    path: '/features',
    title: 'Features',
    filter: (entity: Entity) =>
      isStandardComponent(entity) &&
      isFeatureFlagActive('features-tab-enabled'),
    loader: async () =>
      compatWrapper(
        <Grid.Root columns={{ initial: '1' }} gap="6">
          <Grid.Item>
            <EntityImplementsFeaturesCard />
          </Grid.Item>
        </Grid.Root>,
      ),
  },
});

export const datadogContent = EntityContentBlueprint.make({
  name: 'datadog',
  params: {
    path: '/datadog',
    title: 'Datadog',
    filter: (entity: Entity) =>
      isServiceComponent(entity) &&
      isFeatureFlagActive('datadog-insights-enabled'),
    loader: async () =>
      compatWrapper(
        <EntityDatadogReferrerWrapper>
          <DatadogRouter />
        </EntityDatadogReferrerWrapper>,
      ),
  },
});

export const codeInsightsContent = EntityContentBlueprint.make({
  name: 'code-insights',
  params: {
    path: '/code-insights',
    title: 'Code Insights',
    filter: (entity: Entity) =>
      isStandardComponent(entity) &&
      isFeatureFlagActive('code-insights-enabled'),
    loader: async () => {
      const { InsightsPage } = await import(
        '@roadiehq/backstage-plugin-github-insights'
      );
      return compatWrapper(<InsightsPage />);
    },
  },
});

export const systemDiagramContent = EntityContentBlueprint.make({
  name: 'system-diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    filter: 'kind:system',
    loader: async () => (
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    ),
  },
});

export const redocContent = EntityContentBlueprint.make({
  name: 'redoc',
  params: {
    path: '/redoc',
    title: 'ReDoc',
    filter: isOpenApiEntity,
    loader: async () => {
      const { ApiRedocContent } = await import('./ApiRedocContent');
      return compatWrapper(<ApiRedocContent />);
    },
  },
});

export const badgesContextMenuItem = EntityContextMenuItemBlueprint.make({
  name: 'badges',
  params: {
    icon: <RiCoupon3Line size={18} />,
    useProps: () => ({
      title: 'Badges',
      onClick: () => {
        openBadgesDialog();
      },
    }),
  },
});

export const createPreviewContextMenuItem = EntityContextMenuItemBlueprint.make(
  {
    name: 'create-preview',
    params: {
      icon: <RiEyeLine size={18} />,
      useProps: () => {
        const { entity } = useAsyncEntity();
        const navigate = useNavigate();

        return {
          title: 'Create Preview',
          onClick: async () => {
            if (!entity) return;
            const { toSourceEntity } = await import(
              '@schibsted/backstage-plugin-entity-preview-common'
            );
            const YAML = (await import('yaml')).default;
            navigate('/entity-preview', {
              state: { yaml: YAML.stringify(toSourceEntity(entity)) },
            });
          },
        };
      },
    },
  },
);
